import { Routes, type AppStepSlug } from '@/utils/navigation/routes'

/**
 * ПУТЬ ЧЕЛОВЕКА ПО ТИПУ ВОРОНКИ (utils/funnel.ts) — одно место на весь фронт.
 *
 * Воронка 1 (текущая): intro → ссылка → подтверждение → контакт → сразу выдача. Фильтры подбирает
 * модель первым шагом прогона; так воронка устроена с 2026-09-15 («сразу вести на ага-момент»).
 *
 * Воронка 2 (2026-09-21, «уточнение»): те же экраны плюс ТРИ ВОПРОСА — индустрии, локации,
 * компания, — и стоят они МЕЖДУ подтверждением и контактом. Сначала их поставили после контакта
 * (тогда это были шаги 3-5 визарда), и в тот же день Дима перенёс: «этапы 5, 6, 7 вынесем до этапа 4,
 * а после сбора контакта будем уже экран ожидания и шортлист показывать». Смысл переноса — человек
 * платит почтой за уже уточнённую выдачу, а не за обещание, и ага-момент наступает сразу после письма.
 *
 * ПАНЕЛЬ НАЗЫВАЕТСЯ ИМЕНЕМ, А НЕ НОМЕРОМ. Номер панели раньше задавал и порядок, и метку в аналитике,
 * и вставка панели в середину сдвигала всё после неё, молча переименовывая историю отчётов
 * (см. onboardSteps.ts). Теперь порядок задаёт ЭТОТ список, номер для аналитики закреплён за именем
 * там же, и они больше не мешают друг другу.
 */
export const REFINE_FUNNEL = 2

/**
 * Воронка 3 «погружение» (2026-09-21): те же экраны, что у воронки 2, но на выдаче вместо пейволла
 * кнопка «Reach the contacts», которая уводит В ПРОДУКТ, а пейволл человек встречает уже внутри
 * кабинета. Слова Димы: «на экране Shortlist CTA — Reach the contacts, и там должны прокидывать
 * внутрь продукта, а paywall уже будет внутри продукта».
 *
 * Гипотеза измеряет ровно одно: поднимает ли готовность платить то, что человек сначала получил
 * продукт в руки. Цена гипотезы известна заранее — прогон уже потрачен (квота индекса, кредиты
 * обогащения, токены), а оплата отодвинута на дни; поэтому CAC воронки 3 считается по
 * `order_confirmed` из Amplitude, а не по Purchase в Мете, у которой окно атрибуции короче.
 */
export const IMMERSE_FUNNEL = 3

/**
 * Воронка 4 «SaaS» (2026-09-22): регистрация почтой с нового лендинга, и только потом продукт — вся
 * настройка (ссылка на сайт, индустрии, локации, ICP) происходит УЖЕ ВНУТРИ кабинета, а первый прогон
 * запускается из него же через focus mode. Онбординг на /onboard в этой воронке не используется вовсе:
 * попавший туда уводится на /signup. Дальше — как в воронке 3: выдача ведёт обратно в продукт, пейволл
 * внутри кабинета.
 */
export const SAAS_FUNNEL = 4

/** Человек сначала попадает в продукт и настраивает его там; /onboard ему не показывается. */
export const productFirst = (funnel: number): boolean => funnel === SAAS_FUNNEL

/**
 * ЧУЖАЯ ДВЕРЬ: адрес, который этой воронке не принадлежит, и куда с него уводить. null — адрес свой.
 *
 * Три двери, и за каждой — задвоенный или пропавший Lead (разбор маршрутов перед сплит-тестом
 * 1/3 против 4, 2026-09-28):
 *   · /signup в воронках 1–3. Регистрация там не вход, а второй Lead: человек, уже оставивший почту
 *     на контакте онбординга, регистрировался, уходил в /home, оттуда Home отправлял его на /onboard,
 *     и он отдавал Мете второй `onboarding_completed` и второй Lead. Уводим на /onboard.
 *   · /onboard в воронке 4. Онбординг здесь живёт внутри кабинета; пришедший по старой ссылке прошёл
 *     бы два онбординга подряд. Уводим на регистрацию.
 *   · /home в воронке 4 БЕЗ КАБИНЕТА В ЭТОМ БРАУЗЕРЕ. Кабинет этой воронки пускает с пустыми руками —
 *     это начало её пути, — поэтому «Log in» на лендинге и прямой /home проводили человека через
 *     онбординг, поиск и пейволл без единого Lead: кампания B недосчитывала лиды, а покупки случались
 *     у людей, которых для Меты не было. Уводим на регистрацию; кабинет есть — пускаем, как раньше.
 *
 * РЕШЕНИЕ ПРИНИМАЕТСЯ ДО ЭКРАНА, в App.tsx, и по той же причине App не считает заход на чужую дверь.
 * Раньше уводили сами экраны, эффектом после монтирования, — а к тому моменту App уже отправил
 * page_viewed, PageView и scr за адрес, которого человек не видел, и сам экран — свои шаги
 * онбординга. Ленивый чанк экрана не может отозвать событие, ушедшее до его загрузки.
 *
 * `hasWorkspace` — функцией: он читает localStorage, а нужен ровно одному случаю из трёх.
 * Адрес сравнивается так, как его сопоставляет роутер: без учёта регистра и хвостового слеша —
 * иначе `/SignUp/` открыл бы экран мимо этой проверки.
 */
export const funnelDetour = (pathname: string, funnel: number, hasWorkspace: () => boolean): string | null => {
  const at = pathname.replace(/\/+$/, '').toLowerCase() || Routes.landing
  if (at === Routes.signup) return productFirst(funnel) ? null : Routes.onboard
  if (at === Routes.onboard) return productFirst(funnel) ? Routes.signup : null
  if (at === Routes.home && productFirst(funnel) && !hasWorkspace()) return Routes.signup
  return null
}

/** Панели /onboard. Ровно эти имена уезжают в аналитику как `name`. */
/* `code` — код из письма после контакта: вход через аккаунт Intch. В путях воронок его нет — на него
   ведёт только принятая форма контакта (OnboardStore.submitContact), а не «дальше». */
export type OnboardPane = 'intro' | 'link' | 'confirm' | 'industries' | 'locations' | 'company' | 'contact' | 'signup' | 'code'

const FLOW_CURRENT: readonly OnboardPane[] = ['intro', 'link', 'confirm', 'contact']
const FLOW_REFINE: readonly OnboardPane[] = ['intro', 'link', 'confirm', 'industries', 'locations', 'company', 'contact']

/** Порядок панелей этой воронки. Всё остальное («дальше», «назад», прогресс) считается отсюда. */
export const onboardFlow = (funnel: number): readonly OnboardPane[] =>
  funnel === REFINE_FUNNEL || funnel === IMMERSE_FUNNEL ? FLOW_REFINE : FLOW_CURRENT

/** Следующая панель, или null — если это была последняя (дальше уже прогон). */
export const nextPane = (funnel: number, pane: OnboardPane): OnboardPane | null => {
  const flow = onboardFlow(funnel)
  const at = flow.indexOf(pane)
  return at >= 0 && at + 1 < flow.length ? (flow[at + 1] as OnboardPane) : null
}

/** Предыдущая панель, или null — с первой возвращаться некуда. */
export const prevPane = (funnel: number, pane: OnboardPane): OnboardPane | null => {
  const flow = onboardFlow(funnel)
  const at = flow.indexOf(pane)
  return at > 0 ? (flow[at - 1] as OnboardPane) : null
}

/**
 * ТРИ ВОПРОСА — ЭТО ТРИ ШАГА СПЕКИ, и соответствие живёт здесь, потому что за ним стоит событие.
 * Ответ на вопрос закрывает шаг визарда (`AppWizardStore.completeStep`), а это единственное место в
 * продукте, откуда уходит `wizard_step_completed {step, skipped}` — «AI подберёт сам» против «выбрал
 * руками», главный вопрос воронки 2. Номера те же, что у шагов 3-5 визарда: те же поля, тот же стор,
 * поэтому и счёт один.
 */
export const QUESTION_PANES: readonly OnboardPane[] = ['industries', 'locations', 'company']

const QUESTION_STEP: Readonly<Record<string, number>> = { industries: 3, locations: 4, company: 5 }

export const isQuestionPane = (pane: OnboardPane): boolean =>
  (QUESTION_PANES as readonly string[]).includes(pane)

/** Шаг визарда, который закрывает этот вопрос. 0 — панель не вопрос. */
export const questionStep = (pane: OnboardPane): number => QUESTION_STEP[pane] ?? 0

/** Номер вопроса от 1 и сколько их всего — для подписи «Question N of 3». */
export const questionIndex = (pane: OnboardPane): number => QUESTION_PANES.indexOf(pane) + 1
export const QUESTION_TOTAL = QUESTION_PANES.length

/**
 * СКОЛЬКО ШАГОВ ВИЗАРДА ОНБОРДИНГ ОТВЕЧАЕТ ЗА ЧЕЛОВЕКА, и почему это ДВА момента, а не один.
 *
 * `ON_CONFIRM` (2) ставится, как только продукт подтверждён: шаг 1 — продукт прочитан, шаг 2 — роли,
 * их модель подбирает из только что прочитанных сегментов покупателя. Отметка нужна именно здесь по
 * двум причинам, и обе измерены:
 *   · `markOnboardingAnswered` ПЕРЕСОБИРАЕТ карту `done` целиком, поэтому после неё три вопроса
 *     закрываются ПЕРВЫЙ раз — а `completeStep` шлёт событие только на первое закрытие. Без этой
 *     отметки человек с уже заполненной картой (второй продукт в той же вкладке, возврат по ссылке из
 *     письма, где пишется done={1..5}) ответил бы на все три вопроса и не отправил НИ ОДНОГО события;
 *   · до отметки `furthestStep` равен 1, и заход на /app/* редиректит на первый шаг визарда.
 *
 * `ON_HANDOFF` (5) ставится после контакта и закрывает остальное: человек уходит прямо на выдачу.
 */
export const ANSWERED_ON_CONFIRM = 2
export const ANSWERED_ON_HANDOFF = 5

/**
 * ПЕЙВОЛЛ ВНУТРИ ПРОДУКТА — ТОЛЬКО У ВОРОНКИ 3, и это единственное место, где так решается.
 *
 * Значение читают оба конца: выдача (девять дверей к пакетам вместо пейволла уводят в кабинет — см.
 * `enterProduct` в launch/LaunchStep.tsx) и кабинет (лист неоплаченного поиска держит свои замки и
 * свою дверь к оплате). Одно условие на оба конца: разойдутся — человек окажется либо в кабинете без
 * двери к оплате, либо с двумя пейволлами подряд.
 */
export const paywallInsideProduct = (funnel: number): boolean =>
  funnel === IMMERSE_FUNNEL || funnel === SAAS_FUNNEL

/**
 * Куда ведёт кнопка после контакта — на выдачу, во всех трёх воронках. Функция остаётся одним местом на
 * случай воронок 3 и 4 (погружение в продукт и SaaS-путь), где ответ будет другим.
 */
export const firstSlugAfterOnboarding = (_funnel: number): AppStepSlug => 'launch'
